import type { ApiResponse, PaginatedResponse } from '@klaro7/shared-types';

import api from '@/lib/api';

export interface PublicProfessionalUser {
  profile: {
    firstName: string;
    lastName: string;
    avatar?: string;
  };
}

export interface PublicProfessional {
  id: string;
  title: string;
  specializations: string[];
  experience: number;
  education?: string;
  certifications?: string[];
  hourlyRate?: number;
  costPerMinute?: number;
  currency?: string;
  videoUrl?: string;
  profileDescription?: string;
  availability?: {
    isAvailable: boolean;
    workingHours?: {
      dayOfWeek: number;
      startTime: string;
      endTime: string;
    }[];
    timezone: string;
    notes?: string | null;
  };
  serviceChannels?: {
    chat: boolean;
    audioCall: boolean;
    videoCall: boolean;
  };
  rating?: number;
  reviewCount?: number;
  /** Stato al momento della risposta REST; fonte per gli utenti anonimi (senza socket live). */
  onlineStatus?: 'online' | 'busy' | 'offline' | 'paused';
  userId: PublicProfessionalUser;
  createdAt: string;
}

export interface PublicProfessionalQueryParams {
  page?: number;
  /** Page size; 'all' is the "Tutti" sentinel (server returns every match). */
  limit?: number | 'all';
  sortBy?: 'rating' | 'price' | 'reviews' | 'experience';
  sortOrder?: 'asc' | 'desc';
  specialization?: string;
  minRate?: number;
  maxRate?: number;
  search?: string;
  available?: boolean;
}

export async function getPublicProfessionals(
  params?: PublicProfessionalQueryParams
): Promise<PaginatedResponse<PublicProfessional>> {
  const response = await api.get<PaginatedResponse<PublicProfessional>>(
    '/professionals/public',
    { params }
  );
  return response.data;
}

export async function getFeaturedProfessionals(): Promise<PublicProfessional[]> {
  const response = await api.get<ApiResponse<PublicProfessional[]>>(
    '/professionals/featured'
  );
  return response.data.data;
}

export async function getPublicProfessionalById(
  id: string
): Promise<PublicProfessional> {
  const response = await api.get<ApiResponse<PublicProfessional>>(
    `/professionals/public/${id}`
  );
  return response.data.data;
}

export interface SpecializationCount {
  name: string;
  count: number;
}

export async function getSpecializations(): Promise<SpecializationCount[]> {
  const response = await api.get<ApiResponse<SpecializationCount[]>>(
    '/professionals/specializations'
  );
  return response.data.data;
}
